/**
 * Stand-demo control pills — top-centre.
 *
 * Chosen by Lenny on 12/10/2026 and trimmed on 13/10/2026 for the Demo Day
 * stand. Touch, walk scenes: Classic (swipe to look, joystick to move) and
 * Hold to walk (swipe to look, hold to walk); touch orbit scenes keep
 * Classic and show no pills. Desktop, walk scenes: Classic (drag to look,
 * WASD) and Gamer (click, then the mouse looks, WASD); desktop orbit
 * scenes: Classic (drag to rotate, scroll to zoom) and Gamer (click, then
 * the mouse orbits, W/S to zoom). Cursor (edge look) and the touch Gyro
 * were dropped on 13/10/2026. Each is a scheme in
 * src/experiments/control-schemes.ts; this is a plain row of buttons over
 * applyScheme(), not the A/B picker (which stays off). Each pill is its own
 * 36 px glass bubble, the same height as the scene title and the toolbar
 * buttons, with nothing wrapped around them.
 */

import type { SchemeId } from '../experiments/control-schemes';
import { resolveLang, type UiLang } from './lang';

export interface DemoChoice {
    id: SchemeId;
    label: Record<UiLang, string>;
}

export const WALK_TOUCH_CHOICES: DemoChoice[] = [
    { id: 'A', label: { en: 'Classic',      es: 'Clásico',           ca: 'Clàssic' } },
    { id: 'C', label: { en: 'Hold to walk', es: 'Mantén para andar', ca: 'Mantén per caminar' } }
];

export const WALK_MOUSE_CHOICES: DemoChoice[] = [
    { id: 'A', label: { en: 'Classic', es: 'Clásico', ca: 'Clàssic' } },
    { id: 'E', label: { en: 'Gamer',   es: 'Gamer',   ca: 'Gamer' } }
];

export const ORBIT_MOUSE_CHOICES: DemoChoice[] = [
    { id: 'A', label: { en: 'Classic', es: 'Clásico', ca: 'Clàssic' } },
    { id: 'G', label: { en: 'Gamer',   es: 'Gamer',   ca: 'Gamer' } }
];

/** The row for this scene and device; null when there is none (touch orbit). */
export function demoChoicesFor(kind: 'walk' | 'orbit', touch: boolean): DemoChoice[] | null {
    if (touch) return kind === 'walk' ? WALK_TOUCH_CHOICES : null;
    return kind === 'walk' ? WALK_MOUSE_CHOICES : ORBIT_MOUSE_CHOICES;
}

export interface DemoControlsOptions {
    parent: HTMLElement;
    choices: DemoChoice[];
    initial: SchemeId;
    onChoose: (id: SchemeId) => void;
}

export class DemoControls {

    private root: HTMLDivElement;

    private buttons = new Map<SchemeId, HTMLButtonElement>();

    constructor({ parent, choices, initial, onChoose }: DemoControlsOptions) {
        const lang = resolveLang();
        this.root = document.createElement('div');
        this.root.className = 'ollo-demo-controls';
        this.root.setAttribute('role', 'group');
        this.root.setAttribute('aria-label', 'Controls');
        for (const c of choices) {
            const b = document.createElement('button');
            b.className = 'ollo-btn ollo-demo-pill';
            b.type = 'button';
            b.textContent = c.label[lang];
            b.addEventListener('click', () => {
                this.mark(c.id);
                onChoose(c.id);
            });
            this.buttons.set(c.id, b);
            this.root.appendChild(b);
        }
        this.mark(initial);
        parent.appendChild(this.root);
    }

    /** The pill for a scheme — the tutorial's bubble points at Gamer, or at
     *  Hold to walk on touch. */
    button(id: SchemeId): HTMLButtonElement | null {
        return this.buttons.get(id) ?? null;
    }

    private mark(id: SchemeId): void {
        for (const [k, b] of this.buttons) b.classList.toggle('active', k === id);
    }

    dispose(): void {
        this.root.remove();
    }
}
